import React from 'react';
import ClaudeMark from '../../components/ClaudeMark';
import { CHATGPT_SETUP_STEPS, CHATGPT_REQUIREMENTS } from '../../components/ChatGptSetupSteps';

// ============================================================================
// The stories the connect funnel tells, and how to pick one.
// ----------------------------------------------------------------------------
// Same page, same wizard, same illustrations — only the protagonist changes:
//
//   claude  → "Give Claude web scraping powers". Sign-in based: paste one URL
//             into Claude's connector settings, no token anywhere.
//   chatgpt → "Give ChatGPT web scraping powers". Also sign-in based (ChatGPT
//             has no API-key or custom-header option at all), via developer
//             mode and the Plugins screen.
//   mcp     → "Give your AI agents web scraping powers". Cursor, Claude Code,
//             anything else that speaks MCP. Connects with an access token.
//
// Routes:
//   /connect-claude       the original URL. Shipped extensions link to it, so it
//                         must keep meaning "Claude" forever.
//   /connect?mcp=claude   the same Claude story on the new route.
//   /connect?mcp=chatgpt  the ChatGPT story.
//   /connect  /connect?mcp    the any-agent story.
//
// Every difference between them lives in this file, so no story can be
// half-rewritten: add a key here and both the page and the wizard pick it up.
// ============================================================================

// Claude's starburst — Anthropic's own mark, inlined and shown unmodified (the
// coral comes from currentColor, the shape is untouched), per their trademark
// guidelines. Same SVG the marketing site uses, so the two surfaces show Claude
// identically. Deliberately NOT a hand-drawn "C" badge: that was our lettering
// standing in for someone else's logo.
export const ClaudeGlyph = ({ className = 'w-5 h-5' }) => (
  <svg viewBox="0 0 24 24" fill="none" className={`${className} flex-shrink-0 text-[#d97757] object-contain`} role="img" aria-label="Claude">
    <path fill="currentColor" d="M5.92405 15.2962L9.85823 13.0903L9.92405 12.8981L9.85823 12.7918H9.66582L9.0076 12.7513L6.75949 12.6906L4.81013 12.6097L2.92152 12.5085L2.44557 12.4073L2 11.8204L2.04557 11.5269L2.44557 11.2588L3.01772 11.3094L4.28354 11.3954L6.18228 11.5269L7.55949 11.6079L9.6 11.8204H9.92405L9.96962 11.6888L9.85823 11.6079L9.77215 11.5269L7.8076 10.1963L5.68101 8.78978L4.56709 7.98027L3.96456 7.57045L3.66076 7.18594L3.52911 6.34607L4.07595 5.74399L4.81013 5.79459L4.99747 5.84518L5.74177 6.4169L7.33165 7.64635L9.4076 9.1743L9.71139 9.42727L9.83291 9.34126L9.8481 9.28055L9.71139 9.05287L8.58228 7.01391L7.37722 4.93954L6.84051 4.07943L6.69873 3.56337C6.6481 3.35087 6.61266 3.17379 6.61266 2.95624L7.23544 2.11131L7.57975 2L8.41013 2.11131L8.75949 2.41487L9.27595 3.59373L10.1114 5.45054L11.4076 7.97521L11.7873 8.72401L11.9899 9.41715L12.0658 9.62965H12.1975V9.50822L12.3038 8.08652L12.5013 6.34101L12.6937 4.09461L12.7595 3.46218L13.0734 2.70326L13.6962 2.29345L14.1823 2.52618L14.5823 3.0979L14.5266 3.46724L14.2886 5.01037L13.8228 7.42879L13.519 9.04781H13.6962L13.8987 8.84543L14.719 7.75765L16.0962 6.03744L16.7038 5.35441L17.4127 4.60056L17.8684 4.24134H18.7291L19.362 5.18239L19.0785 6.15381L18.1924 7.27701L17.4582 8.22818L16.4051 9.64483L15.7468 10.7781L15.8076 10.8692L15.9646 10.854L18.3443 10.3481L19.6304 10.1154L21.1646 9.85226L21.8582 10.1761L21.9342 10.5049L21.6608 11.1778L20.0203 11.5826L18.0962 11.9671L15.2304 12.6451L15.1949 12.6704L15.2354 12.721L16.5266 12.8424L17.0785 12.8728H18.4304L20.9468 13.06L21.6051 13.4951L22 14.0263L21.9342 14.4311L20.9215 14.9471L19.5544 14.6233L16.3646 13.8644L15.2709 13.5912H15.119V13.6823L16.0304 14.5727L17.7013 16.0804L19.7924 18.0233L19.8987 18.5039L19.6304 18.8834L19.3468 18.8429L17.5089 17.4617L16.8 16.8394L15.1949 15.4885H15.0886V15.6302L15.4582 16.1715L17.4127 19.106L17.5139 20.0066L17.3722 20.3L16.8658 20.4771L16.3089 20.3759L15.1646 18.7721L13.9848 16.9658L13.0329 15.3468L12.9165 15.4126L12.3544 21.4586L12.0911 21.7673L11.4835 22L10.9772 21.6155L10.7089 20.9932L10.9772 19.7637L11.3013 18.1599L11.5646 16.8849L11.8025 15.3013L11.9443 14.7751L11.9342 14.7397L11.8177 14.7549L10.6228 16.3941L8.80506 18.848L7.36709 20.386L7.02279 20.5226L6.42532 20.214L6.48101 19.6625L6.81519 19.1718L8.80506 16.642L10.0051 15.0736L10.7797 14.168L10.7747 14.0364H10.7291L5.44304 17.4667L4.50127 17.5882L4.0962 17.2087L4.14684 16.5864L4.33924 16.384L5.92911 15.2912L5.92405 15.2962Z" />
  </svg>
);

// Claude Code's pixel glyph — Anthropic's mark, inlined so it keeps the coral
// accent via currentColor. Same SVG the marketing site uses.
export const ClaudeCodeMark = ({ className = 'w-5 h-5' }) => (
  <svg viewBox="0 0 24 24" fill="none" className={`${className} text-[#d97757] object-contain`} role="img" aria-label="Claude Code">
    <path fill="currentColor" fillRule="evenodd" d="M20 4v4h4v4h-4v8h-2v-4h-2v4h-2v-4h-4v4H8v-4H6v4H4v-8H0V8h4V4zm-4 2v2h2V6zM6 8h2V6H6z" clipRule="evenodd" />
  </svg>
);

// The other clients, as self-hosted app tiles. Used nominatively — these mark
// the app you're connecting, never our own branding — and shown unmodified.
const LogoMark = ({ src, alt, className = 'w-5 h-5', rounded = 'rounded-md' }) => (
  <img src={src} alt={alt} className={`flex-shrink-0 object-contain ${rounded} ${className}`} />
);

export const CursorMark = (props) => <LogoMark src="/assets/logos/cursor-logo.webp" alt="Cursor" {...props} />;
export const ChatGptMark = (props) => <LogoMark src="/assets/logos/openai-logo.webp" alt="ChatGPT" {...props} />;
export const PerplexityMark = (props) => <LogoMark src="/assets/logos/perplexity-logo.webp" alt="Perplexity" {...props} />;
export const HermesMark = (props) => <LogoMark src="/assets/logos/hermes-logo.webp" alt="Hermes" {...props} />;
export const OpenClawMark = (props) => <LogoMark src="/assets/logos/openclaw-logo.webp" alt="OpenClaw" {...props} />;

// Claude as a raster app tile rather than the bare starburst — for rows that
// stack it beside other tiles, where the glyph reads as a different weight.
export const ClaudeTileMark = (props) => <ClaudeMark rounded="rounded-md" {...props} />;

// The apps the illustrated chat rotates through on the any-agent story. Real
// logos rather than one generic spark: "your AI agent" is an abstraction, and
// the whole point of the page is that it's the app the visitor already uses.
// Every entry is a client that connects today — the wider MCP ecosystem is the
// strip below, not this list.
export const MCP_AGENTS = [
  { name: 'Claude', Mark: ClaudeGlyph },
  { name: 'ChatGPT', Mark: ChatGptMark },
  { name: 'Cursor', Mark: CursorMark },
  { name: 'Claude Code', Mark: ClaudeCodeMark },
];

// The same lineup for the places that stack the marks as overlapping app tiles.
// Claude swaps to its raster tile there: beside Cursor's and ChatGPT's tiles a
// bare glyph reads as a different weight (the marketing site draws the same
// distinction between its chat mark and its logo strip).
export const MCP_AGENT_TILES = MCP_AGENTS.map((agent) =>
  agent.name === 'Claude' ? { ...agent, Mark: ClaudeTileMark } : agent
);

// The strip under the hero — the compatibility claim at a glance. Broader than
// MCP_AGENTS: these are apps that can add an MCP connection, not ones we ship a
// guided setup for.
export const MCP_APP_STRIP = [
  { name: 'Claude', Mark: ClaudeTileMark },
  { name: 'ChatGPT', Mark: ChatGptMark },
  { name: 'Cursor', Mark: CursorMark },
  { name: 'Perplexity', Mark: PerplexityMark },
  { name: 'Hermes', Mark: HermesMark },
  { name: 'OpenClaw', Mark: OpenClawMark },
];

// A "works with" chip's leading mark.
const ClaudeChipMark = () => <ClaudeMark className="w-4 h-4" rounded="rounded-[3px]" />;
const ChatGptChipMark = () => <ChatGptMark className="w-4 h-4" rounded="rounded-[3px]" />;

// The far side of the wizard's handshake illustration — the app you're adding us
// to. Each story owns its own tile because the marks are different objects:
// Claude's is a finished cream tile, ChatGPT's a white one. Both are shown
// unmodified and used nominatively.
const PartnerTile = ({ Mark }) => (
  <div className="relative w-12 h-12 rounded-xl overflow-hidden shadow-lg shadow-black/10 dark:shadow-black/40 ring-1 ring-inset ring-black/[0.06] dark:ring-white/[0.08] shrink-0">
    <Mark className="w-full h-full" rounded="rounded-none" />
  </div>
);

// Bold UI paths inside the wizard's setup steps ("Settings → Connectors").
const Em = ({ children }) => (
  <span className="font-semibold text-gray-800 dark:text-gray-100">{children}</span>
);

// The same path, but it opens that screen in the app — one click instead of
// hunting through someone else's settings menu. New tab, so the URL they still
// have to copy from step 1 stays on screen behind it.
const EmLink = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="font-semibold text-gray-800 dark:text-gray-100 underline decoration-gray-400/70 dark:decoration-gray-500/70 underline-offset-2 hover:text-gray-900 dark:hover:text-white hover:decoration-current transition-colors"
  >
    {children}
  </a>
);

// Claude's connector settings, deep-linked.
const CLAUDE_CONNECTORS_URL = 'https://claude.ai/new#settings/customize-connectors';

// The nominative-use line for whichever marks a story actually renders.
const ANTHROPIC_TRADEMARK = 'Claude is a trademark of Anthropic, PBC.';
const OPENAI_TRADEMARK = 'ChatGPT is a trademark of OpenAI, L.L.C.';
const independentOf = (owners) =>
  `Ultimate Web Scraper is an independent product and is not affiliated with, endorsed by, or sponsored by ${owners}.`;

export const CONNECT_CLIENTS = {
  claude: {
    key: 'claude',
    pageTitle: 'Connect Claude',
    heroBadge: 'New · Now works with Claude',
    heroTitleLead: 'Give Claude',
    heroSub:
      'Connect Ultimate Web Scraper to Claude, then just ask — right inside your own chat — and get clean, structured data back.',
    ctaLabel: 'Connect Claude',
    switcherLabel: 'Claude',
    worksWith: [
      { label: 'Claude', Mark: ClaudeChipMark },
      { label: 'Claude Code', Mark: ClaudeChipMark },
    ],

    // Chat illustrations
    agentName: 'Claude',
    Glyph: ClaudeGlyph,
    // The tools hub stacks app tiles, where the bare starburst reads as a
    // different weight — same distinction MCP_AGENT_TILES draws.
    HubMark: ClaudeTileMark,
    accent: '#d97757',

    // The protagonist, mid-sentence and sentence-initial — for the lines that
    // are otherwise identical across the stories.
    subject: 'Claude',
    Subject: 'Claude',

    // Tools section
    toolsLabel: 'Your toolkit, in Claude',
    toolsSub:
      'Connecting Claude hands it your whole Ultimate Web Scraper toolkit — the same extractors you already know, now driven by a conversation.',
    hubSub: 'Connect once — Claude can reach every tool below',

    // Examples section
    examplesSubLead: 'Real asks you can send Claude — each its own little chat. Click a message to copy it, swap the',

    // How it works — only the first step differs (how you connect).
    howStep1: {
      title: 'Connect Claude once',
      description:
        'Add Ultimate Web Scraper to Claude in a few seconds. You sign in — no API keys, nothing to copy or keep safe.',
    },

    // Closing panel
    closingSub: 'Sign in and connect Claude — it takes about a minute.',
    closingStep2: {
      title: 'Connect Claude',
      desc: 'Add one URL to Claude, sign in when prompted, and start asking.',
    },
    securityNote:
      'Claude only ever works inside your own workspace, and every extraction shows its credit cost and asks you to confirm before it runs. You can disconnect at any time.',
    trademarkNote: `${ANTHROPIC_TRADEMARK} ${independentOf('Anthropic')}`,

    // Wizard
    modalEyebrow: 'Connect Claude',
    signinSubtitle: 'Sign in or create a free account to connect Claude to your workspace.',
    upsellLine: 'to connect Claude to your workspace',
    modalFooter: 'Claude only works inside your own workspace. Disconnect anytime.',

    // The wizard's final step. `steps` follow the copy-the-URL step the panel
    // always renders first, so they can assume the URL is on screen above them.
    setup: {
      PartnerTile: () => <PartnerTile Mark={ClaudeMark} />,
      title: 'Add Ultimate Web Scraper to Claude',
      subtitle: 'Three quick steps — about a minute. No API key, nothing to keep safe.',
      steps: [
        <>In Claude, open <EmLink href={CLAUDE_CONNECTORS_URL}>Settings → Connectors</EmLink> and click <Em>Add custom connector</Em>.</>,
        <>Paste the URL, click <Em>Connect</Em>, and sign in when prompted. Pick your workspace and you&rsquo;re done.</>,
      ],
      oauthNote: 'Sign-in based — Claude gets access through your account, and you can disconnect anytime.',
      cli: {
        summary: 'Using Claude Code instead?',
        intro: "Run this in your terminal — you'll be prompted to sign in on first use:",
        command: (mcpUrl) => `claude mcp add --transport http ultimate-web-scraper ${mcpUrl}`,
      },
    },
  },

  chatgpt: {
    key: 'chatgpt',
    pageTitle: 'Connect ChatGPT',
    heroBadge: 'New · Now works with ChatGPT',
    heroTitleLead: 'Give ChatGPT',
    heroSub:
      'Connect Ultimate Web Scraper to ChatGPT, then just ask — right inside your own chat — and get clean, structured data back.',
    ctaLabel: 'Connect ChatGPT',
    switcherLabel: 'ChatGPT',
    // One chip, and it earns its place: MCP apps are web-only in ChatGPT, so
    // "on the web" is a real prerequisite rather than a logo row.
    worksWith: [{ label: 'ChatGPT on the web', Mark: ChatGptChipMark }],

    agentName: 'ChatGPT',
    Glyph: ChatGptMark,
    HubMark: ChatGptMark,
    accent: '#10a37f',

    subject: 'ChatGPT',
    Subject: 'ChatGPT',

    toolsLabel: 'Your toolkit, in ChatGPT',
    toolsSub:
      'Connecting ChatGPT hands it your whole Ultimate Web Scraper toolkit — the same extractors you already know, now driven by a conversation.',
    hubSub: 'Connect once — ChatGPT can reach every tool below',

    examplesSubLead: 'Real asks you can send ChatGPT — each its own little chat. Click a message to copy it, swap the',

    howStep1: {
      title: 'Connect ChatGPT once',
      description:
        'Add Ultimate Web Scraper to ChatGPT in a few seconds. You sign in — no API keys, nothing to copy or keep safe.',
    },

    closingSub: 'Sign in and connect ChatGPT — it takes about a minute.',
    closingStep2: {
      title: 'Connect ChatGPT',
      desc: 'Add one URL in ChatGPT, sign in when prompted, and start asking.',
    },
    securityNote:
      'ChatGPT only ever works inside your own workspace, and every extraction shows its credit cost and asks you to confirm before it runs. You can disconnect at any time.',
    trademarkNote: `${OPENAI_TRADEMARK} ${independentOf('OpenAI')}`,

    modalEyebrow: 'Connect ChatGPT',
    signinSubtitle: 'Sign in or create a free account to connect ChatGPT to your workspace.',
    upsellLine: 'to connect ChatGPT to your workspace',
    modalFooter: 'ChatGPT only works inside your own workspace. Disconnect anytime.',

    setup: {
      PartnerTile: () => <PartnerTile Mark={ChatGptMark} />,
      title: 'Add Ultimate Web Scraper to ChatGPT',
      // Five, not the four ChatGptSetupSteps counts — the wizard numbers the
      // copy-the-URL step too.
      subtitle: 'Five quick steps — about a minute. No API key, nothing to keep safe.',
      // Shared with the Integrations ChatGPT card so the two can't drift.
      steps: CHATGPT_SETUP_STEPS,
      oauthNote: 'Sign-in based — ChatGPT gets access through your account, and you can disconnect anytime.',
      note: CHATGPT_REQUIREMENTS,
    },
  },

  mcp: {
    key: 'mcp',
    pageTitle: 'Connect your AI agent',
    heroBadge: 'New · Works with Claude, Cursor & any MCP agent',
    heroTitleLead: 'Give your AI agents',
    heroSub:
      'Connect Ultimate Web Scraper to Claude, Claude Code or any MCP AI agent, then just ask — right inside your own chat — and get clean, structured data back.',
    ctaLabel: 'Connect your agent',
    switcherLabel: 'MCP',
    // No chip row here: the full app strip under the hero makes the same claim
    // with more apps and bigger marks, and running both was the same list twice.
    worksWith: null,

    // The illustrated chat cycles owners with each scenario, so the any-agent
    // claim is shown rather than asserted. `agents` overrides the single
    // identity below; Claude's story has no such list and stays Claude.
    agents: MCP_AGENTS,
    agentTiles: MCP_AGENT_TILES,
    appStrip: MCP_APP_STRIP,
    agentName: 'Your AI agent',
    Glyph: (p) => <ClaudeMark rounded="rounded-md" {...p} />,
    HubMark: ClaudeTileMark,
    accent: '#06b6d4',

    subject: 'your agent',
    Subject: 'Your agent',

    toolsLabel: 'Your toolkit, over MCP',
    toolsSub:
      'Connecting over MCP hands your agent the whole Ultimate Web Scraper toolkit — the same extractors you already know, now driven by a conversation.',
    hubSub: 'Connect once — your agent can reach every tool below',

    examplesSubLead: 'Real asks you can send your agent — each its own little chat. Click a message to copy it, swap the',

    howStep1: {
      title: 'Connect your agent once',
      description:
        'Create an access token here, paste it into your AI app, and you are connected. One setup, no code, revocable anytime.',
    },

    closingSub: 'Sign in and connect your AI agent — it takes about a minute.',
    closingStep2: {
      title: 'Connect over MCP',
      desc: 'Create an access token and paste the ready-made block into your app.',
    },
    securityNote:
      'Your agent only ever works inside your own workspace, and every extraction shows its credit cost and asks you to confirm before it runs. You can revoke access at any time.',
    // This story renders a whole strip of third-party marks, so it names the two
    // it walks people through and covers the rest with the catch-all the
    // marketing site uses.
    trademarkNote: `${ANTHROPIC_TRADEMARK} ${OPENAI_TRADEMARK} ${independentOf('Anthropic or OpenAI')} All other product names and logos are trademarks of their respective owners, used for identification only.`,

    modalEyebrow: 'Connect your AI agent',
    signinSubtitle: 'Sign in or create a free account to connect your AI agent to your workspace.',
    upsellLine: 'to connect your AI agent to your workspace',
    modalFooter: 'Your agent only works inside your own workspace. Revoke access anytime.',

    // The token flow carries a form and a code block, so it gets the wider
    // wizard shell; the sign-in stories stay a narrow, single-column read.
    wideModal: true,
  },
};

// The stories, in the order the header switcher offers them: the two sign-in
// apps first (most visitors), then the catch-all.
export const CONNECT_STORIES = [CONNECT_CLIENTS.claude, CONNECT_CLIENTS.chatgpt, CONNECT_CLIENTS.mcp];

// The `mcp` param values each story answers to — an explicit map rather than a
// straight CONNECT_CLIENTS lookup, so aliases are cheap.
const STORY_BY_PARAM = {
  claude: CONNECT_CLIENTS.claude,
  chatgpt: CONNECT_CLIENTS.chatgpt,
  openai: CONNECT_CLIENTS.chatgpt,
  gpt: CONNECT_CLIENTS.chatgpt,
  mcp: CONNECT_CLIENTS.mcp,
};

// The param value that selects each story — the inverse of STORY_BY_PARAM, used
// when the switcher rewrites the URL. The any-agent story keeps its historic
// `1` sentinel so old links and the extension's `?mcp` keep meaning the same.
export const storyParam = (key) => (key === CONNECT_CLIENTS.mcp.key ? '1' : key);

// /connect-claude is the legacy URL and always means Claude — shipped extension
// builds point at it. On /connect the `mcp` param picks the story; anything
// unrecognised (including a bare `?mcp`, the `?mcp=1` sentinel, or no param at
// all) falls back to the any-agent one.
export const resolveConnectClient = (pathname, searchParams) => {
  if (pathname === '/connect-claude') return CONNECT_CLIENTS.claude;
  const requested = (searchParams.get('mcp') || '').trim().toLowerCase();
  const story = STORY_BY_PARAM[requested];
  // Checked against the real list: a prototype key (`?mcp=toString`) would
  // otherwise "resolve" to a function off Object.prototype.
  return CONNECT_STORIES.includes(story) ? story : CONNECT_CLIENTS.mcp;
};
